import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { createQuickTicket, searchTicketAffected, ticketFilterOptionsQuery, type TicketFormOption } from '../api/easeedesk'
import { Modal } from '../ui/Modal'
import { SelectPicker } from '../ui/SelectPicker'
import { toast } from '../ui/notify'

/**
 * "Quick Ticket" - `#createQuickTicketModal` din lista de tichete
 * (`easeedesk/quick_ticket/form.php`): firma, omul afectat (cautat pe masura
 * ce se scrie, `get_field_options` cu `ticket_affected`), subiect, descriere.
 * Tipul, starea si restul vin din dispecerizarea firmei pentru sursa 22
 * (`POST easeedesk/tickets/quick`, `QuickTicket` in API).
 */
export function QuickTicketModal({ onClose, onDone }: { onClose(): void; onDone(ticket: number): void }) {
  const options = useQuery(ticketFilterOptionsQuery)
  const [company, setCompany] = useState('')
  const [affected, setAffected] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [found, setFound] = useState<TicketFormOption[]>([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(0)

  const companies = options.data?.companies ?? []
  useEffect(() => {
    if (company === '' && companies.length > 0) setCompany(String(companies[0].id))
  }, [companies, company])

  /* Firma s-a schimbat: omul ales era al celeilalte. */
  useEffect(() => {
    setAffected('')
    setFound([])
  }, [company])

  function search(q: string): void {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const stamp = ++latest.current
      setSearching(true)
      searchTicketAffected(Number(company) || 0, 'user', q)
        .then((answer) => {
          if (stamp === latest.current) setFound(answer.options)
        })
        .catch(() => {
          if (stamp === latest.current) setFound([])
        })
        .finally(() => {
          if (stamp === latest.current) setSearching(false)
        })
    }, 250)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    createQuickTicket({ ticket_company: company, ticket_affected: affected, subject, description })
      .then((answer) => {
        toast(`Ticket #${answer.ticket} created.`, 'Success', 'success')
        onDone(answer.ticket)
      })
      .catch((err: unknown) => toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="createQuickTicketModal"
      open
      onClose={onClose}
      title={
        <>
          <i className="fas fa-bolt" aria-hidden="true"></i> Quick ticket
        </>
      }
      labelledBy="createQuickTicketModalLabel"
    >
      <form className="form-horizontal" id="quick-ticket-form" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor="quick-ticket-company">
            Company <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <select id="quick-ticket-company" name="ticket_company" className="form-control" value={company} onChange={(e) => setCompany(e.target.value)}>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor="quick-ticket-affected">
            User
            <input type="hidden" name="affected_type" value="user" readOnly />
          </label>
          <div className="col-sm-9" id="quick-affected-group">
            <SelectPicker
              id="quick-ticket-affected"
              name="ticket_affected"
              className="form-control"
              title="Select and Begin Typing"
              size={10}
              options={[{ value: '', label: '-' }, ...found.map((o) => ({ value: String(o.value), label: o.name, subtext: o.subtext }))]}
              value={affected}
              onChange={(next) => setAffected(Array.isArray(next) ? next[0] ?? '' : next)}
              onSearch={search}
              searching={searching}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor="quick-ticket-subject">
            Subject <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <input id="quick-ticket-subject" className="form-control" type="text" name="subject" required value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor="quick-ticket-description">
            Description <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <textarea id="quick-ticket-description" style={{ maxWidth: '100%' }} name="description" rows={10} required className="form-control" value={description} onChange={(e) => setDescription(e.target.value)}></textarea>
          </div>
        </div>
        <div className="form-group aright">
          <div className="col-sm-12">
            <button className="btn btn-default" type="button" onClick={onClose}>
              Cancel
            </button>{' '}
            <button className="btn btn-success" type="submit" disabled={busy || company === ''}>
              Create
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
